// ---------------------------------------------------------------------------
// useTranslation.ts — Translation hook with interpolation support
// ---------------------------------------------------------------------------
// All locale dictionaries are statically imported for instant, synchronous
// language switching. No async loading, no race conditions, no silent failures.
//
// Usage:
//   const { t, locale, setLocale, dir } = useTranslation();
//   t('common.save')              => "Save"
//   t('messages.welcome', { name: 'John' })  => "Welcome, John!"
//   t('errors.minLength', { min: '8' })      => "Must be at least 8 characters."
// ---------------------------------------------------------------------------

'use client';

import { useCallback, useMemo } from 'react';
import {
  LOCALE_MAP,
  getLocaleDir,
  type SupportedLocale,
} from './i18n.config';
import { useLocaleStore } from './locale.store';
import { useLocaleContext } from './locale.context';

import enTranslations from './translations/en.json';
import arTranslations from './translations/ar.json';
import thTranslations from './translations/th.json';
import esTranslations from './translations/es.json';
import frTranslations from './translations/fr.json';
import deTranslations from './translations/de.json';
import jaTranslations from './translations/ja.json';
import zhTranslations from './translations/zh.json';
import hiTranslations from './translations/hi.json';

// ---------------------------------------------------------------------------
// Translation registry — all locales statically bundled
// ---------------------------------------------------------------------------

type TranslationData = Record<string, unknown>;

const allTranslations: Record<SupportedLocale, TranslationData> = {
  en: enTranslations as TranslationData,
  ar: arTranslations as TranslationData,
  th: thTranslations as TranslationData,
  es: esTranslations as TranslationData,
  fr: frTranslations as TranslationData,
  de: deTranslations as TranslationData,
  ja: jaTranslations as TranslationData,
  zh: zhTranslations as TranslationData,
  hi: hiTranslations as TranslationData,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Reads a nested value from an object using a dot-delimited key.
 * e.g. getNestedValue(obj, 'common.save') => obj.common.save
 */
function getNestedValue(obj: TranslationData, path: string): string | undefined {
  if (!path) return undefined;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Replaces `{{key}}` placeholders in a string with provided values.
 *
 * About forty catalogue strings (in every locale) were written with single
 * braces — `{room}`, `{count}` — and rendered literally ("Room {room}
 * pre-selected"). A single-brace token is substituted too, but only when the
 * caller supplied that key, so any literal brace text is left untouched.
 */
function interpolate(template: string, params?: Record<string, string>): string {
  if (!params) return template;
  return template
    .replace(/\{\{(\w+)\}\}/g, (_, key: string) => params[key] ?? `{{${key}}}`)
    .replace(/\{(\w+)\}/g, (token, key: string) => (key in params ? params[key] : token));
}


// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseTranslationReturn {
  /** Translate a dot-delimited key with optional interpolation */
  t: (key: string, params?: Record<string, string>) => string;
  /** Current active locale */
  locale: SupportedLocale;
  /** Switch to a different locale */
  setLocale: (locale: SupportedLocale) => void;
  /** Text direction for the current locale */
  dir: 'ltr' | 'rtl';
  /** Native name of the current locale */
  localeName: string;
  /** Always false — translations are statically bundled */
  isLoading: boolean;
}

/**
 * Hook for accessing translations.
 *
 * All locale dictionaries are statically imported and available synchronously.
 * When `setLocale` is called, the preference is persisted to localStorage and
 * the UI re-renders instantly with the new translation set.
 */
export function useTranslation(): UseTranslationReturn {
  const contextLocale = useLocaleContext();
  const storeLocale = useLocaleStore((s) => s.locale);
  const locale = contextLocale ?? storeLocale;
  const setLocaleStore = useLocaleStore((s) => s.setLocale);

  const translations = allTranslations[locale] ?? allTranslations.en;

   const t = useCallback(
     (key: string, params?: Record<string, string>): string => {
       const value = getNestedValue(translations, key);
       if (value === undefined) {
         // Fallback: try English translations
         if (locale !== 'en') {
           const fallback = getNestedValue(allTranslations.en, key);
           if (fallback !== undefined) return interpolate(fallback, params);
         }
         // Dev warning for missing translation key
         if (process.env.NODE_ENV === 'development') {
           console.warn(`[i18n] Missing translation key: "${key}" for locale "${locale}"`);
         }
         // Return the key itself as last resort (aids debugging)
         return key;
       }
       return interpolate(value, params);
     },
     [translations, locale],
   );

  const setLocale = useCallback((newLocale: SupportedLocale) => {
    setLocaleStore(newLocale);
  }, [setLocaleStore]);

  const dir = useMemo(() => getLocaleDir(locale), [locale]);

  const localeName = useMemo(
    () => LOCALE_MAP[locale].nativeName,
    [locale],
  );

  return { t, locale, setLocale, dir, localeName, isLoading: false };
}
